import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import images from "../../utilities/images/images";
import {
  createPracticeSet,
  fetchMistakeAnalytics,
  fetchUserWrongTopics,
} from "./mistakeBookApi";
import { getMistakeBookSubjectsForCourse } from "./mistakeBookSubjects";
import { resolveSubjectIconComponent } from "./SubjectIcons";
import { ChartDownIcon } from "./WeakAreaIcons";
import alertCircleIcon from "./assets/waa-alert-circle.svg";
import "./WeakAreaAnalysisPage.css";

const DEFAULT_YEARS = [2026, 2025, 2024, 2023];

const RANK_TONES = [
  { color: "#994c0d", bg: "#fef6ee" },
  { color: "#d8336d", bg: "#fdf2f8" },
  { color: "#1959b2", bg: "#eff6ff" },
];

const buildDonutGradient = (categories = []) => {
  const total = categories.reduce((sum, item) => sum + Number(item.percent || 0), 0);
  if (!total) return "conic-gradient(#e2e8f0 0 100%)";

  let cursor = 0;
  const stops = categories.map((item) => {
    const share = (Number(item.percent || 0) / total) * 100;
    const start = cursor;
    cursor += share;
    return `${item.color} ${start}% ${cursor}%`;
  });
  return `conic-gradient(${stops.join(", ")})`;
};

const WeakAreaAnalysisPage = () => {
  const history = useHistory();
  const location = useLocation();
  const state = location.state || {};
  const userPreference = useSelector((store) => store.userPreference);
  const targetId = userPreference?.targetDetails?.[0]?.target_id;
  const targetName = userPreference?.targetDetails?.[0]?.target ?? "";

  const courseSubjects = useMemo(
    () => getMistakeBookSubjectsForCourse({ targetId, targetName }),
    [targetId, targetName]
  );

  const [loading, setLoading] = useState(true);
  const [creatingSet, setCreatingSet] = useState(false);
  const [error, setError] = useState("");
  const [year, setYear] = useState(Number(state.year) || DEFAULT_YEARS[0]);
  const [years, setYears] = useState(DEFAULT_YEARS);
  const [yearMenuOpen, setYearMenuOpen] = useState(false);
  const [attemptFilter, setAttemptFilter] = useState("Attempt Last");
  const [attemptMenuOpen, setAttemptMenuOpen] = useState(false);
  const [subjects, setSubjects] = useState(
    Array.isArray(state.subjects) && state.subjects.length
      ? state.subjects
      : courseSubjects
  );
  const [activeSubjectId, setActiveSubjectId] = useState(
    state.subjectId || courseSubjects[0]?.id
  );
  const [analytics, setAnalytics] = useState(null);
  const yearDropdownRef = useRef(null);
  const attemptDropdownRef = useRef(null);

  useEffect(() => {
    setSubjects(courseSubjects);
    setActiveSubjectId((current) => {
      const stillExists = courseSubjects.some(
        (subject) => String(subject.id) === String(current)
      );
      return stillExists ? current : courseSubjects[0]?.id;
    });
  }, [courseSubjects]);

  const activeSubject = useMemo(
    () =>
      subjects.find((s) => String(s.id) === String(activeSubjectId)) ||
      subjects[0],
    [subjects, activeSubjectId]
  );

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        year,
        subject: activeSubject?.name || "Physics",
        attempt_filter: attemptFilter,
      };

      const [topicsResult, analyticsPayload] = await Promise.all([
        fetchUserWrongTopics(params).catch(() => ({
          subjects: [],
          topics: [],
          years: [],
        })),
        fetchMistakeAnalytics(params),
      ]);
      const topicsPayload = topicsResult;

      if (topicsPayload.years?.length) {
        setYears(topicsPayload.years);
        if (!topicsPayload.years.includes(Number(year))) {
          setYear(topicsPayload.years[0]);
        }
      }

      let weakTopics = analyticsPayload.topWeakTopics || [];
      if (!weakTopics.length && topicsPayload.topics?.length) {
        weakTopics = [...topicsPayload.topics]
          .sort((a, b) => Number(b.errors || b.mistakes) - Number(a.errors || a.mistakes))
          .slice(0, 3);
      }

      setAnalytics({
        ...analyticsPayload,
        topWeakTopics: weakTopics,
      });

      if (analyticsPayload.attemptFilters?.length) {
        setAttemptFilter((current) =>
          analyticsPayload.attemptFilters.includes(current)
            ? current
            : analyticsPayload.attemptFilters[0]
        );
      }
    } catch (err) {
      setAnalytics(null);
      setError(
        err?.response?.data?.message ||
          "Unable to load Weak Area Analysis right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [year, activeSubject?.name, attemptFilter]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    if (!yearMenuOpen && !attemptMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target)
      ) {
        setYearMenuOpen(false);
      }
      if (
        attemptDropdownRef.current &&
        !attemptDropdownRef.current.contains(event.target)
      ) {
        setAttemptMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [yearMenuOpen, attemptMenuOpen]);

  const handleBack = () => {
    history.push("/practice/mistake-book", {
      year,
      subjectId: activeSubjectId,
      subjects,
    });
  };

  const handleStartPracticing = async () => {
    if (creatingSet) return;

    const subjectName = activeSubject?.name || "Physics";
    const subjectId = activeSubjectId || activeSubject?.id;

    setCreatingSet(true);
    setError("");
    try {
      // createPracticeSet expects subject name (same as Practice Sets page).
      await createPracticeSet({
        subject: subjectName,
        question_count: 10,
      });
    } catch (err) {
      // Still open that subject's practice sets even if create fails.
      setError(
        err?.response?.data?.message ||
          "Could not create a new set. Opening existing practice sets."
      );
    } finally {
      setCreatingSet(false);
    }

    history.push("/practice/mistake-book/practice-sets", {
      year,
      subjectId,
      subjectName,
      subject: activeSubject || { id: subjectId, name: subjectName },
      fromWeakAreaAnalysis: true,
    });
  };

  const donutStyle = useMemo(
    () => ({
      background: buildDonutGradient(analytics?.errorBreakdown || []),
    }),
    [analytics?.errorBreakdown]
  );

  const leftLegend = (analytics?.errorBreakdown || []).slice(0, 4);
  const rightLegend = (analytics?.errorBreakdown || []).slice(4);

  return (
    <div className="waa-page">
      <div className="waa-sticky-top">
        <header className="waa-header">
          <button
            type="button"
            className="waa-header__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            <img src={images.new_backarrow} alt="" />
          </button>
          <h1 className="waa-header__title">Weak Area Analysis</h1>
          <div className="waa-header__year">
            <div
              className={`waa-year-dropdown${yearMenuOpen ? " is-open" : ""}`}
              ref={yearDropdownRef}
            >
              <button
                type="button"
                className="waa-year-dropdown__trigger"
                onClick={() => setYearMenuOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={yearMenuOpen}
                aria-label="Select year"
              >
                <span>{year}</span>
                <svg
                  className="waa-year-dropdown__chevron"
                  width="9"
                  height="6"
                  viewBox="0 0 8.701 5.156"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M8.45685 0.240019C8.3022 0.0862882 8.093 0 7.87494 0C7.65689 0 7.44769 0.0862882 7.29304 0.240019L4.32987 3.16192L1.40796 0.240019C1.25331 0.0862882 1.04411 0 0.826056 0C0.607998 0 0.398799 0.0862882 0.244151 0.240019C0.166788 0.316751 0.105383 0.40804 0.0634785 0.508622C0.0215742 0.609205 0 0.717089 0 0.826051C0 0.935013 0.0215742 1.0429 0.0634785 1.14348C0.105383 1.24406 0.166788 1.33535 0.244151 1.41208L3.74383 4.91177C3.82056 4.98913 3.91185 5.05053 4.01244 5.09244C4.11302 5.13434 4.2209 5.15592 4.32987 5.15592C4.43883 5.15592 4.54671 5.13434 4.64729 5.09244C4.74788 5.05053 4.83917 4.98913 4.9159 4.91177L8.45685 1.41208C8.53421 1.33535 8.59562 1.24406 8.63752 1.14348C8.67943 1.0429 8.701 0.935013 8.701 0.826051C8.701 0.717089 8.67943 0.609205 8.63752 0.508622C8.59562 0.40804 8.53421 0.316751 8.45685 0.240019Z"
                    fill="#334155"
                  />
                </svg>
              </button>
              {yearMenuOpen ? (
                <ul className="waa-year-dropdown__menu" role="listbox">
                  {years.map((item) => (
                    <li key={item}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={Number(item) === Number(year)}
                        className={`waa-year-dropdown__option${
                          Number(item) === Number(year) ? " is-selected" : ""
                        }`}
                        onClick={() => {
                          setYear(Number(item));
                          setYearMenuOpen(false);
                        }}
                      >
                        {item}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </header>

        <nav className="waa-subjects" aria-label="Subjects">
          {subjects.map((subject) => {
            const isActive = String(subject.id) === String(activeSubjectId);
            const SubjectIcon = resolveSubjectIconComponent(subject);
            return (
              <button
                key={subject.id}
                type="button"
                className={`waa-subject-tab${
                  isActive ? " waa-subject-tab--active" : ""
                }`}
                onClick={() => setActiveSubjectId(subject.id)}
              >
                <SubjectIcon className="waa-subject-tab__icon" size={20} />
                <span>{subject.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="waa-body">
        {loading ? (
          <div className="waa-loader">
            <ClipLoader size={40} color="#C61C26" />
          </div>
        ) : (
          <>
            {error ? <p className="waa-error">{error}</p> : null}

            <section className="waa-insight-banner" aria-label="Accuracy insight">
              <div className="waa-insight-banner__icon">
                <ChartDownIcon className="waa-insight-banner__chart-icon" size={34} />
              </div>
              <div className="waa-insight-banner__copy">
                <p className="waa-insight-banner__title">
                  Your accuracy is{" "}
                  <strong className="waa-insight-banner__accent-yellow">
                    {analytics?.currentAccuracy ?? 0}%
                  </strong>
                  . Closing your top 3 weak topics could lift it to{" "}
                  <strong className="waa-insight-banner__accent-green">
                    ~{analytics?.projectedAccuracy ?? 0}%
                  </strong>
                  .
                </p>
                <p className="waa-insight-banner__subtitle">
                  {analytics?.insightSecondary}
                </p>
              </div>
            </section>

            <div className="waa-main-grid">
              <div className="waa-main-grid__left">
                <section className="waa-card waa-card--weak">
                  <div className="waa-card__heading">
                    <h2>Top 3 weak topics</h2>
                    <p>Where you&apos;re losing the most marks</p>
                  </div>
                  <div className="waa-weak-list">
                    {(analytics?.topWeakTopics || []).length ? (
                      (analytics?.topWeakTopics || []).map((topic, index) => {
                        const tone = RANK_TONES[index % RANK_TONES.length];
                        const isLast =
                          index === (analytics?.topWeakTopics || []).length - 1;
                        return (
                          <div key={topic.id}>
                            <div className="waa-weak-row">
                              <div className="waa-weak-row__main">
                                <div
                                  className="waa-rank"
                                  style={{
                                    background: tone.bg,
                                    color: tone.color,
                                  }}
                                >
                                  {index + 1}
                                </div>
                                <div className="waa-weak-row__text">
                                  <span
                                    className="waa-weak-row__subject"
                                    style={{ color: tone.color }}
                                  >
                                    {topic.subjectName ||
                                      activeSubject?.name ||
                                      "Subject"}
                                  </span>
                                  <h3>{topic.name}</h3>
                                  <p>
                                    {topic.errors || topic.mistakes} errors ·{" "}
                                    {topic.accuracy || topic.percent}% accuracy
                                  </p>
                                </div>
                              </div>
                              <div className="waa-weightage">
                                <span className="waa-weightage__label">
                                  WEIGHTAGE
                                </span>
                                <div className="waa-weightage__pills">
                                  <span className="waa-weightage__pill waa-weightage__pill--adv">
                                    Adv: {topic.weightageAdv}%
                                  </span>
                                  <span className="waa-weightage__pill waa-weightage__pill--mains">
                                    Mains: {topic.weightageMains}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            {!isLast ? (
                              <div className="waa-weak-divider" aria-hidden="true" />
                            ) : null}
                          </div>
                        );
                      })
                    ) : (
                      <p className="waa-empty">
                        No weak topics found for this subject.
                      </p>
                    )}
                  </div>
                </section>

                <section className="waa-card waa-card--time">
                  <div className="waa-card__heading waa-card__heading--row">
                    <div>
                      <h2>
                        {analytics?.timeQualityTitle || "Time Quality Report"}
                      </h2>
                      <p>
                        {analytics?.timeQualitySubtitle ||
                          "How your speed pairs with accuracy"}
                      </p>
                    </div>
                    <div className="waa-header__year" ref={attemptDropdownRef}>
                      <div
                        className={`waa-dropdown${
                          attemptMenuOpen ? " waa-dropdown--open" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className="waa-dropdown__trigger"
                          onClick={() => setAttemptMenuOpen((open) => !open)}
                          aria-expanded={attemptMenuOpen}
                        >
                          <span>{attemptFilter}</span>
                          <svg
                            className="waa-dropdown__chevron"
                            width="9"
                            height="6"
                            viewBox="0 0 8.701 5.156"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M8.45685 0.240019C8.3022 0.0862882 8.093 0 7.87494 0C7.65689 0 7.44769 0.0862882 7.29304 0.240019L4.32987 3.16192L1.40796 0.240019C1.25331 0.0862882 1.04411 0 0.826056 0C0.607998 0 0.398799 0.0862882 0.244151 0.240019C0.166788 0.316751 0.105383 0.40804 0.0634785 0.508622C0.0215742 0.609205 0 0.717089 0 0.826051C0 0.935013 0.0215742 1.0429 0.0634785 1.14348C0.105383 1.24406 0.166788 1.33535 0.244151 1.41208L3.74383 4.91177C3.82056 4.98913 3.91185 5.05053 4.01244 5.09244C4.11302 5.13434 4.2209 5.15592 4.32987 5.15592C4.43883 5.15592 4.54671 5.13434 4.64729 5.09244C4.74788 5.05053 4.83917 4.98913 4.9159 4.91177L8.45685 1.41208C8.53421 1.33535 8.59562 1.24406 8.63752 1.14348C8.67943 1.0429 8.701 0.935013 8.701 0.826051C8.701 0.717089 8.67943 0.609205 8.63752 0.508622C8.59562 0.40804 8.53421 0.316751 8.45685 0.240019Z"
                              fill="#334155"
                            />
                          </svg>
                        </button>
                        {attemptMenuOpen ? (
                          <ul className="waa-dropdown__menu" role="listbox">
                            {(analytics?.attemptFilters || ["Attempt Last"]).map(
                              (item) => (
                                <li key={item}>
                                  <button
                                    type="button"
                                    className={`waa-dropdown__option${
                                      item === attemptFilter
                                        ? " waa-dropdown__option--active"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      setAttemptFilter(item);
                                      setAttemptMenuOpen(false);
                                    }}
                                  >
                                    {item}
                                  </button>
                                </li>
                              )
                            )}
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="waa-zones">
                    {(analytics?.timeZones || []).map((zone) => (
                      <div
                        key={zone.id}
                        className={`waa-zone waa-zone--${zone.tone || "warning"}`}
                      >
                        <strong>{zone.percent}%</strong>
                        <span>{zone.label}</span>
                        <small>{zone.hint}</small>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              <section className="waa-card waa-card--errors">
                <div className="waa-card__heading">
                  <h2>
                    {analytics?.whyWrongTitle || "Why you're getting it wrong"}
                  </h2>
                  <p>
                    {analytics?.whyWrongSubtitle ||
                      "Error breakdown across all mistakes"}
                  </p>
                </div>

                <div className="waa-error-panel">
                  <div className="waa-donut" style={donutStyle}>
                    <div className="waa-donut__hole">
                      <strong>{analytics?.totalErrors ?? 0}</strong>
                      <span>Errors</span>
                    </div>
                  </div>

                  <div className="waa-legend">
                    <div className="waa-legend__col">
                      {leftLegend.map((item) => (
                        <div key={item.id} className="waa-legend__row">
                          <span
                            className="waa-legend__swatch"
                            style={{ background: item.color }}
                          />
                          <span className="waa-legend__label">{item.label}</span>
                          <strong>{item.percent}%</strong>
                        </div>
                      ))}
                    </div>
                    <div className="waa-legend__divider" aria-hidden="true" />
                    <div className="waa-legend__col">
                      {rightLegend.map((item) => (
                        <div key={item.id} className="waa-legend__row">
                          <span
                            className="waa-legend__swatch"
                            style={{ background: item.color }}
                          />
                          <span className="waa-legend__label">{item.label}</span>
                          <strong>{item.percent}%</strong>
                        </div>
                      ))}
                    </div>
                  </div>

                  {analytics?.errorTip ? (
                    <div className="waa-error-tip">
                      <img src={alertCircleIcon} alt="" />
                      <p>{analytics.errorTip}</p>
                    </div>
                  ) : null}
                </div>
              </section>
            </div>
          </>
        )}
      </div>

      <div className="waa-cta">
        <div className="waa-cta__inner">
          <div className="waa-cta__copy">
            <h2>Ready to fix these?</h2>
            <p>We have built a targeted set across your weak areas</p>
          </div>
          <button
            type="button"
            className="waa-cta__button"
            onClick={handleStartPracticing}
            disabled={creatingSet || loading}
          >
            {creatingSet ? "Starting..." : "Start Practicing"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeakAreaAnalysisPage;
